/**
 * Readable labels for the builder's undo/redo history.
 *
 * Pure, UI-layer, derived-only. Nothing here writes to the store, and nothing
 * is persisted: the panel memoizes over zundo's `pastStates`/`futureStates`
 * and the live partialized state, exactly like BuilderPage memoizes materials
 * over the store (see CLAUDE.md - derived data is never stored).
 *
 * Two responsibilities, both pure:
 *  1. `describeHistoryStep` diffs two consecutive partialized snapshots and
 *     returns a translation KEY plus typed args (the domain i18n-key pattern
 *     applied to a UI helper) - it never returns a rendered string.
 *  2. `buildHistoryEntries` turns the raw zundo arrays into a newest-first,
 *     de-duplicated list of jump targets with the undo/redo step count.
 *
 * Performance contract: every comparison is shallow. Top-level fields are
 * compared by reference (zustand's `set` merges, so an untouched field keeps
 * its identity), sub-objects by their scalar fields, and the opening list by
 * length + id + scalar fields - never a deep clone or JSON compare.
 */

import {
	EXTERIOR_TREATMENT_CATALOG,
	INTERIOR_WOOD_CATALOG,
	PANEL_DIRECTION_CATALOG,
} from "../../domain/finishes/finishCatalog";
import type {
	OpeningWall,
	WallOpening,
} from "../../domain/framing/generateFramedWall";
import { resolveWallHeightM } from "../../domain/geometry/buildingDimensions";
import { getHeaterModel } from "../../domain/heater/heaterCatalog";
import type { UnitSystem } from "../../domain/units/formatUnits";
import {
	formatAreaM2,
	formatLengthM,
	formatLengthMm,
} from "../../domain/units/formatUnits";
import type { DeckConfig, VisibilityFlags } from "../../engines/types";
import type { Dictionary } from "../../i18n/types";
import {
	BUILDER_HISTORY_LIMIT,
	type PartializedBuilderState,
} from "../../store/builderStoreConfig";

/** Message keys this module can emit. All live under the `history_` prefix. */
export type HistoryLabelKey = Extract<keyof Dictionary, `history_${string}`>;

/**
 * Dictionary keys whose value is a plain string. A few dictionary entries are
 * `string[]`, which can never be interpolated into a label.
 */
export type DictionaryTextKey = {
	[K in keyof Dictionary]: Dictionary[K] extends string ? K : never;
}[keyof Dictionary];

/**
 * A single interpolated value. Kept as data rather than a string so the diff
 * stays pure and the unit system / locale are applied at the render boundary
 * (see the metric-imperial display-only note).
 */
export type HistoryLabelArg =
	| { kind: "text"; value: string }
	| { kind: "i18n"; key: DictionaryTextKey }
	| { kind: "lengthM"; value: number }
	| { kind: "lengthMm"; value: number }
	| { kind: "areaM2"; value: number }
	| { kind: "dimensions"; width: number; length: number }
	/** `value` is a ratio (0.55 renders as "55 %"), not a percentage number. */
	| { kind: "percent"; value: number }
	| { kind: "degrees"; value: number };

export interface HistoryStepLabel {
	key: HistoryLabelKey;
	args?: Record<string, HistoryLabelArg>;
}

export type HistoryEntryKind = "past" | "current" | "future";

export interface HistoryEntry {
	/** Stable within a render; safe as a React key. */
	id: string;
	kind: HistoryEntryKind;
	/** Argument for `temporal.undo(steps)` / `temporal.redo(steps)`; 0 = current. */
	steps: number;
	label: HistoryStepLabel;
}

/** Cap per direction so a 50-deep history never renders 50 rows. */
export const HISTORY_MAX_ENTRIES_PER_DIRECTION = 20;

const GENERIC: HistoryStepLabel = { key: "history_change" };

/* ── Field-level diffing ─────────────────────────────────── */

type PartializedField = keyof PartializedBuilderState;

/**
 * Constructing an `Intl.NumberFormat` is the expensive part, so they are
 * memoized per locale rather than rebuilt for every row the panel renders
 * (the same pattern `src/i18n/currency.ts` uses).
 */
const PERCENT_FORMATTERS = new Map<string, Intl.NumberFormat>();
const WHOLE_NUMBER_FORMATTERS = new Map<string, Intl.NumberFormat>();

function percentFormatter(locale: string): Intl.NumberFormat {
	let formatter = PERCENT_FORMATTERS.get(locale);
	if (!formatter) {
		formatter = new Intl.NumberFormat(locale, {
			style: "percent",
			maximumFractionDigits: 0,
		});
		PERCENT_FORMATTERS.set(locale, formatter);
	}
	return formatter;
}

function wholeNumberFormatter(locale: string): Intl.NumberFormat {
	let formatter = WHOLE_NUMBER_FORMATTERS.get(locale);
	if (!formatter) {
		formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
		WHOLE_NUMBER_FORMATTERS.set(locale, formatter);
	}
	return formatter;
}

/**
 * Every field zundo actually snapshots. Kept explicit (rather than
 * `Object.keys`) so a new partialized field is a compile error away from
 * being labelled, not silently generic — a field missing from this list
 * diffs to nothing, so its edit renders as a generic "change" AND collapses
 * out of the history list entirely.
 *
 * `satisfies` alone would not catch an omission (a short array still
 * satisfies `readonly PartializedField[]`), so `AllFieldsCovered` below
 * fails to resolve when a member is missing.
 */
const PARTIALIZED_FIELDS = [
	"dimensions",
	"openings",
	"roofConfig",
	"deckConfig",
	"thermalInputs",
	"twoRoomConfig",
	"partitionSplit",
	"visibility",
	"siteConditions",
	"interiorLayout",
	"finishes",
	"wallHeightM",
	"indoorRoom",
	"projectName",
	"postalCode",
	"timeOfDay",
	"rotationDeg",
	"wizardCapacityHint",
	"locationMode",
	"climateProfile",
	"market",
] as const satisfies readonly PartializedField[];

/** Resolves to `true` only while every `PartializedField` is listed above. */
type AllFieldsCovered =
	Exclude<PartializedField, (typeof PARTIALIZED_FIELDS)[number]> extends never
		? true
		: never;
const _allPartializedFieldsAreLabelled: AllFieldsCovered = true;
void _allPartializedFieldsAreLabelled;

/**
 * Fields a template / preset transaction touches. A `resetToDefaults` also
 * rewrites market, location, climate, site and project name, so requiring the
 * change set to be a SUBSET of these separates "template applied" from "reset".
 */
const TEMPLATE_FIELDS: ReadonlySet<PartializedField> =
	new Set<PartializedField>([
		"dimensions",
		"partitionSplit",
		"twoRoomConfig",
		"timeOfDay",
		"wizardCapacityHint",
		"openings",
		"deckConfig",
		"roofConfig",
		"thermalInputs",
		"visibility",
	]);

/**
 * Fields a plain `setDimensions` call can touch in the same transaction:
 * the dimensions themselves, the re-centred seeded openings, and — only when
 * the new size no longer matches the wizard's tier — the cleared capacity
 * hint. A change set that is a subset of these (and includes `dimensions`)
 * is a resize, never a template application.
 */
const DIMENSION_CHANGE_FIELDS: ReadonlySet<PartializedField> =
	new Set<PartializedField>(["dimensions", "openings", "wizardCapacityHint"]);

/** Shallow, reference-level field diff. */
export function changedPartializedFields(
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): PartializedField[] {
	return PARTIALIZED_FIELDS.filter(
		(field) => !Object.is(previous[field], next[field]),
	);
}

const WALL_LABEL_KEYS: Record<OpeningWall, DictionaryTextKey> = {
	front: "openings_wallFront",
	back: "openings_wallBack",
	left: "openings_wallLeft",
	right: "openings_wallRight",
	divider: "openings_wallDivider",
};

const VISIBILITY_LABEL_KEYS: Record<keyof VisibilityFlags, DictionaryTextKey> =
	{
		foundation: "vis_foundation",
		framing: "vis_framing",
		panel: "vis_panel",
		roof: "vis_roof",
		frontWall: "vis_frontWall",
		interior: "vis_interior",
		exterior: "vis_exterior",
		deck: "vis_deck",
		heaterClearance: "vis_heaterClearance",
		ventilation: "vis_ventilation",
	};

const DECK_SHAPE_LABEL_KEYS: Record<DeckConfig["shape"], DictionaryTextKey> = {
	rectangular: "deck_shape_rectangular",
	"l-shape": "deck_shape_l_shape",
	"wrap-front": "deck_shape_wrap_front",
	"wrap-sides": "deck_shape_wrap_sides",
};

/**
 * Board / joist sizes are stock designations, not measurements, so they stay
 * metric in both unit systems (same rule as the BOM's dimension strings) - but
 * they must render the way the deck editor renders them, not as the raw enum.
 */
const DECK_BOARD_LABEL_KEYS: Record<
	DeckConfig["boardType"],
	DictionaryTextKey
> = {
	"28x120": "deck_boardOption_28x120",
	"22x95": "deck_boardOption_22x95",
};

const DECK_JOIST_LABEL_KEYS: Record<
	DeckConfig["joistDimension"],
	DictionaryTextKey
> = {
	"45x145": "deck_joistOption_45x145",
	"45x170": "deck_joistOption_45x170",
};

function fieldValue(
	label: DictionaryTextKey,
	value: HistoryLabelArg,
): HistoryStepLabel {
	return {
		key: "history_fieldValue",
		args: { label: { kind: "i18n", key: label }, value },
	};
}

function fieldChanged(label: DictionaryTextKey): HistoryStepLabel {
	return {
		key: "history_fieldChanged",
		args: { label: { kind: "i18n", key: label } },
	};
}

function toggled(label: DictionaryTextKey, on: boolean): HistoryStepLabel {
	return {
		key: on ? "history_toggledOn" : "history_toggledOff",
		args: { label: { kind: "i18n", key: label } },
	};
}

function openingLabel(
	key: HistoryLabelKey,
	opening: WallOpening,
): HistoryStepLabel {
	return {
		key,
		args: {
			type: {
				kind: "i18n",
				key:
					opening.type === "door" ? "openings_typeDoor" : "openings_typeWindow",
			},
			wall: { kind: "i18n", key: WALL_LABEL_KEYS[opening.wall] },
		},
	};
}

/** Scalar equality for one opening. Never a deep/JSON compare. */
function openingEquals(a: WallOpening, b: WallOpening): boolean {
	return (
		a.id === b.id &&
		a.wall === b.wall &&
		a.type === b.type &&
		a.xMm === b.xMm &&
		a.yMm === b.yMm &&
		a.widthMm === b.widthMm &&
		a.heightMm === b.heightMm &&
		(a.glazed ?? false) === (b.glazed ?? false)
	);
}

function describeOpenings(
	previous: readonly WallOpening[],
	next: readonly WallOpening[],
): HistoryStepLabel {
	if (next.length > previous.length) {
		const previousIds = new Set(previous.map((opening) => opening.id));
		const added = next.filter((opening) => !previousIds.has(opening.id));
		return added.length === 1
			? openingLabel("history_openingAdded", added[0])
			: { key: "history_openingsChanged" };
	}

	if (next.length < previous.length) {
		const nextIds = new Set(next.map((opening) => opening.id));
		const removed = previous.filter((opening) => !nextIds.has(opening.id));
		return removed.length === 1
			? openingLabel("history_openingRemoved", removed[0])
			: { key: "history_openingsChanged" };
	}

	let edited: [WallOpening, WallOpening] | null = null;
	for (let index = 0; index < next.length; index += 1) {
		const before = previous[index];
		const after = next[index];
		if (before === after) continue;
		// A different id at the same index means the list was replaced or
		// reordered, not edited in place.
		if (before.id !== after.id) return { key: "history_openingsChanged" };
		if (openingEquals(before, after)) continue;
		if (edited) return { key: "history_openingsChanged" };
		edited = [before, after];
	}

	if (!edited) return { key: "history_openingsChanged" };
	const [before, after] = edited;
	if (before.wall !== after.wall) {
		return openingLabel("history_openingMoved", after);
	}
	if (before.widthMm !== after.widthMm || before.heightMm !== after.heightMm) {
		return openingLabel("history_openingResized", after);
	}
	if (before.xMm !== after.xMm || before.yMm !== after.yMm) {
		return openingLabel("history_openingMoved", after);
	}
	return openingLabel("history_openingChanged", after);
}

function describeDimensions(
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): HistoryStepLabel {
	const widthChanged = previous.dimensions.width !== next.dimensions.width;
	const lengthChanged = previous.dimensions.length !== next.dimensions.length;
	if (widthChanged && !lengthChanged) {
		return fieldValue("slider_widthLabel", {
			kind: "lengthM",
			value: next.dimensions.width,
		});
	}
	if (lengthChanged && !widthChanged) {
		return fieldValue("slider_lengthLabel", {
			kind: "lengthM",
			value: next.dimensions.length,
		});
	}
	if (!widthChanged && !lengthChanged) {
		return fieldChanged("dim_sectionDimensions");
	}
	return fieldValue("dim_sectionDimensions", {
		kind: "dimensions",
		width: next.dimensions.width,
		length: next.dimensions.length,
	});
}

function describeRoof(
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): HistoryStepLabel {
	const before = previous.roofConfig;
	const after = next.roofConfig;
	if (before.kind !== after.kind) {
		return fieldValue("roof_typeLabel", {
			kind: "i18n",
			key:
				after.kind === "gable"
					? "domain_roof_gable"
					: after.kind === "mono"
						? "domain_roof_mono"
						: "domain_roof_barrel",
		});
	}
	if (before.pitchDeg !== after.pitchDeg) {
		return fieldValue("roof_pitchLabel", {
			kind: "degrees",
			value: after.pitchDeg,
		});
	}
	if (before.kind !== "barrel" && after.kind !== "barrel") {
		if (before.systemId !== after.systemId) {
			return fieldValue("roof_systemLabel", {
				kind: "i18n",
				key:
					after.systemId === "sheet_metal"
						? "domain_roof_sheet_metal"
						: "domain_roof_felt_roof",
			});
		}
	}
	return fieldChanged("dim_sectionRoof");
}

function describeDeck(
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): HistoryStepLabel {
	const before = previous.deckConfig;
	const after = next.deckConfig;
	if (!before && after) return { key: "history_deckAdded" };
	if (before && !after) return { key: "history_deckRemoved" };
	if (!before || !after) return fieldChanged("deck_sectionLabel");

	if (before.shape !== after.shape) {
		return fieldValue("deck_shape", {
			kind: "i18n",
			key: DECK_SHAPE_LABEL_KEYS[after.shape],
		});
	}
	if (before.attachWall !== after.attachWall) {
		return fieldValue("deck_attachWall", {
			kind: "i18n",
			key: WALL_LABEL_KEYS[after.attachWall],
		});
	}
	if (before.depth !== after.depth) {
		return fieldValue("deck_depth", { kind: "lengthM", value: after.depth });
	}
	if (before.width !== after.width) {
		return fieldValue("deck_width", { kind: "lengthM", value: after.width });
	}
	if (before.boardType !== after.boardType) {
		return fieldValue("deck_boardType", {
			kind: "i18n",
			key: DECK_BOARD_LABEL_KEYS[after.boardType],
		});
	}
	if (before.joistDimension !== after.joistDimension) {
		return fieldValue("deck_joistDimension", {
			kind: "i18n",
			key: DECK_JOIST_LABEL_KEYS[after.joistDimension],
		});
	}
	return fieldChanged("deck_sectionLabel");
}

function describeThermal(
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): HistoryStepLabel {
	const before = previous.thermalInputs;
	const after = next.thermalInputs;
	if (before.heaterType !== after.heaterType) {
		// `design_heaterLabel`, not `thermal_heaterLabel`: the latter carries a
		// trailing colon for the editor form.
		return fieldValue("design_heaterLabel", {
			kind: "i18n",
			key:
				after.heaterType === "wood"
					? "thermal_heaterWood"
					: "thermal_heaterElectric",
		});
	}
	if ((before.heaterModelId ?? null) !== (after.heaterModelId ?? null)) {
		const model = getHeaterModel(after.heaterModelId);
		return fieldValue(
			"thermal_heaterModelLabel",
			model
				? { kind: "text", value: model.name }
				: { kind: "i18n", key: "thermal_heaterModelNone" },
		);
	}
	if (before.glassAreaSqm !== after.glassAreaSqm) {
		return fieldValue("history_labelGlassArea", {
			kind: "areaM2",
			value: after.glassAreaSqm,
		});
	}
	if (before.stoneAreaSqm !== after.stoneAreaSqm) {
		return fieldValue("history_labelStoneArea", {
			kind: "areaM2",
			value: after.stoneAreaSqm,
		});
	}
	if (before.isOutdoorLogCabin !== after.isOutdoorLogCabin) {
		return toggled("thermal_logCabin", after.isOutdoorLogCabin);
	}
	if ((before.saunaLighting ?? "wall") !== (after.saunaLighting ?? "wall")) {
		return toggled("equipment_lighting", after.saunaLighting !== "none");
	}
	if (before.includesShower !== after.includesShower) {
		return toggled("thermal_shower", after.includesShower);
	}
	if (before.heaterPlacementZMm !== after.heaterPlacementZMm) {
		return fieldChanged("history_labelHeaterPlacement");
	}
	return fieldChanged("dim_sectionThermal");
}

function describeTwoRoom(
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): HistoryStepLabel {
	const before = previous.twoRoomConfig;
	const after = next.twoRoomConfig;
	const changed = (
		["entryWall", "glazingStyle", "saunaPosition", "hasRelaxRoom"] as const
	).filter((key) => before[key] !== after[key]);

	if (changed.length !== 1) return fieldChanged("twoRoom_sectionLayout");

	switch (changed[0]) {
		case "entryWall":
			return fieldValue("twoRoom_entryWallLabel", {
				kind: "i18n",
				key:
					after.entryWall === "short"
						? "twoRoom_entryWallShort"
						: "twoRoom_entryWallLong",
			});
		case "glazingStyle":
			return fieldValue("twoRoom_glazingStyleLabel", {
				kind: "i18n",
				key:
					after.glazingStyle === "panorama"
						? "twoRoom_glazingPanorama"
						: "twoRoom_glazingStandard",
			});
		case "saunaPosition":
			return fieldValue("twoRoom_saunaPositionLabel", {
				kind: "i18n",
				key:
					after.saunaPosition === "left"
						? "twoRoom_saunaPositionLeft"
						: "twoRoom_saunaPositionRight",
			});
		default:
			return toggled("twoRoom_relaxRoomLabel", after.hasRelaxRoom);
	}
}

function describeVisibility(
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): HistoryStepLabel {
	const before = previous.visibility;
	const after = next.visibility;
	const changed = (
		Object.keys(VISIBILITY_LABEL_KEYS) as (keyof VisibilityFlags)[]
	).filter((flag) => before[flag] !== after[flag]);

	if (changed.length !== 1) return fieldChanged("history_labelLayers");
	const flag = changed[0];
	return {
		key: after[flag] ? "history_layerShown" : "history_layerHidden",
		args: { label: { kind: "i18n", key: VISIBILITY_LABEL_KEYS[flag] } },
	};
}

function describeSite(
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): HistoryStepLabel {
	const before = previous.siteConditions;
	const after = next.siteConditions;
	if (before.plinthLengthMm !== after.plinthLengthMm) {
		return fieldValue("site_plinthLengthLabel", {
			kind: "lengthMm",
			value: after.plinthLengthMm,
		});
	}
	if (before.minBuriedDepthMm !== after.minBuriedDepthMm) {
		return fieldValue("site_minBuriedLabel", {
			kind: "lengthMm",
			value: after.minBuriedDepthMm,
		});
	}
	if (before.terrainMode !== after.terrainMode) {
		return fieldValue("site_terrainLabel", {
			kind: "i18n",
			key:
				after.terrainMode === "slope"
					? "site_terrainSlope"
					: "site_terrainFlat",
		});
	}
	if (before.slopeAcrossPercent !== after.slopeAcrossPercent) {
		return fieldValue("site_slopeAcrossLabel", {
			kind: "percent",
			value: after.slopeAcrossPercent / 100,
		});
	}
	if (before.slopeAlongPercent !== after.slopeAlongPercent) {
		return fieldValue("site_slopeAlongLabel", {
			kind: "percent",
			value: after.slopeAlongPercent / 100,
		});
	}
	return fieldChanged("dim_sectionSite");
}

/**
 * The interior layout is one object with a variable-length run list, so it has
 * no fixed set of scalar fields to diff. Report what a user would recognise:
 * a run added or removed, otherwise "the lavar changed" / "the aggregat moved".
 * Still shallow — run counts and the heater's three scalars, never a deep
 * compare of the whole document.
 */
function describeInteriorLayout(
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): HistoryStepLabel {
	const before = previous.interiorLayout;
	const after = next.interiorLayout;
	const beforeRuns = before?.benchRuns.length ?? 0;
	const afterRuns = after?.benchRuns.length ?? 0;
	if (afterRuns > beforeRuns) return fieldChanged("history_labelBenchAdded");
	if (afterRuns < beforeRuns) return fieldChanged("history_labelBenchRemoved");
	if (
		before?.heater.wall !== after?.heater.wall ||
		before?.heater.offsetMm !== after?.heater.offsetMm ||
		before?.heater.wallGapMm !== after?.heater.wallGapMm
	) {
		return fieldChanged("history_labelHeaterMoved");
	}
	return fieldChanged("history_labelBenchLayout");
}

function describeSingleField(
	field: PartializedField,
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): HistoryStepLabel {
	switch (field) {
		case "dimensions":
			return describeDimensions(previous, next);
		case "openings":
			return describeOpenings(previous.openings, next.openings);
		case "roofConfig":
			return describeRoof(previous, next);
		case "deckConfig":
			return describeDeck(previous, next);
		case "thermalInputs":
			return describeThermal(previous, next);
		case "twoRoomConfig":
			return describeTwoRoom(previous, next);
		case "visibility":
			return describeVisibility(previous, next);
		case "siteConditions":
			return describeSite(previous, next);
		case "interiorLayout":
			return describeInteriorLayout(previous, next);
		case "finishes":
			return describeFinishes(previous, next);
		case "wallHeightM":
			return fieldValue("dim_wallHeightLabel", {
				kind: "lengthM",
				value: resolveWallHeightM(next.wallHeightM),
			});
		case "indoorRoom":
			return fieldChanged("indoor_roomTitle");
		case "partitionSplit":
			return fieldValue("dim_partitionSplit", {
				kind: "percent",
				value: next.partitionSplit,
			});
		case "rotationDeg":
			return fieldValue("history_labelRotation", {
				kind: "degrees",
				value: next.rotationDeg,
			});
		case "projectName":
			return fieldValue("toolbar_projectLabel", {
				kind: "text",
				value: next.projectName,
			});
		case "postalCode":
			return fieldValue("slider_postalCodeLabel", {
				kind: "text",
				value: next.postalCode,
			});
		case "timeOfDay":
			return fieldChanged("viewer_todLabel");
		case "locationMode":
			return fieldChanged("history_labelLocation");
		case "climateProfile":
			return fieldChanged("slider_climateZoneLabel");
		default:
			return GENERIC;
	}
}

/**
 * Character choices (issue 134). Three independent choices live in one field,
 * so name the one that actually moved — a single generic "character changed"
 * label would make the undo list useless the moment someone tries two colours
 * in a row.
 */
function describeFinishes(
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): HistoryStepLabel {
	const before = previous.finishes;
	const after = next.finishes;
	if (before.interiorWood !== after.interiorWood) {
		return fieldValue("finish_woodGroupLabel", {
			kind: "i18n",
			key: INTERIOR_WOOD_CATALOG[after.interiorWood].labelKey,
		});
	}
	if (before.exteriorTreatment !== after.exteriorTreatment) {
		return fieldValue("finish_treatmentGroupLabel", {
			kind: "i18n",
			key: EXTERIOR_TREATMENT_CATALOG[after.exteriorTreatment].labelKey,
		});
	}
	if (before.panelDirection !== after.panelDirection) {
		return fieldValue("finish_directionGroupLabel", {
			kind: "i18n",
			key: PANEL_DIRECTION_CATALOG[after.panelDirection].labelKey,
		});
	}
	return GENERIC;
}

/**
 * Diff two consecutive partialized snapshots into one readable label.
 *
 * Multi-field transactions collapse to a single label: `setDimensions`
 * re-centres seeded openings in the same `set`, and a template / preset is one
 * transaction by contract (see the builder-presets-single-transaction note).
 */
export function describeHistoryStep(
	previous: PartializedBuilderState,
	next: PartializedBuilderState,
): HistoryStepLabel {
	const changed = changedPartializedFields(previous, next);
	if (changed.length === 0) return GENERIC;
	if (changed.length === 1)
		return describeSingleField(changed[0], previous, next);

	// `setDimensions` writes dimensions, the re-centred seeded openings, and —
	// when the new size no longer matches the wizard's tier — clears
	// `wizardCapacityHint` in the SAME transaction. All three (or just the
	// first two, when there was no hint to clear) still read as one plain
	// resize, not a template application.
	if (
		changed.includes("dimensions") &&
		changed.every((field) => DIMENSION_CHANGE_FIELDS.has(field))
	) {
		return describeDimensions(previous, next);
	}

	if (changed.every((field) => TEMPLATE_FIELDS.has(field))) {
		return { key: "history_templateApplied" };
	}

	return GENERIC;
}

/* ── Entry list ──────────────────────────────────────────── */

/**
 * Turn zundo's raw arrays into a newest-first list of jump targets.
 *
 * zundo semantics this relies on:
 *  - `pastStates` is chronological (index 0 = oldest, last = the state just
 *    before the current one), so jumping to `pastStates[i]` is `undo(n - i)`.
 *  - `futureStates` is REVERSE-chronological (last = the nearest redo), so
 *    jumping to `futureStates[j]` is `redo(m - j)`.
 *
 * Consecutive value-identical snapshots are collapsed. zundo pushes a history
 * entry for EVERY `set`, including ones that only touch non-partialized fields
 * (`saving`, `saveStatus`, `buildProgress`), which would otherwise render as a
 * run of meaningless "Change" rows. The representative of a run is the entry
 * closest to the present (fewest steps), except that the current position
 * always represents its own run.
 */
export function buildHistoryEntries(input: {
	pastStates: readonly PartializedBuilderState[];
	currentState: PartializedBuilderState;
	futureStates: readonly PartializedBuilderState[];
	maxPerDirection?: number;
	/** zundo's `limit`; a full buffer means the oldest steps were dropped. */
	historyLimit?: number;
}): HistoryEntry[] {
	const {
		pastStates,
		currentState,
		futureStates,
		maxPerDirection = HISTORY_MAX_ENTRIES_PER_DIRECTION,
		historyLimit = BUILDER_HISTORY_LIMIT,
	} = input;

	const pastCount = pastStates.length;
	const historyTruncated = pastCount >= historyLimit;

	// Chronological timeline: past -> current -> future (reversed).
	const timeline: PartializedBuilderState[] = [
		...pastStates,
		currentState,
		...[...futureStates].reverse(),
	];
	const currentIndex = pastCount;

	// Collapse runs of value-identical consecutive snapshots.
	const representatives: number[] = [];
	let runStart = 0;
	for (let index = 1; index <= timeline.length; index += 1) {
		const endOfRun =
			index === timeline.length ||
			changedPartializedFields(timeline[index - 1], timeline[index]).length > 0;
		if (!endOfRun) continue;
		const runEnd = index - 1;
		representatives.push(
			currentIndex >= runStart && currentIndex <= runEnd
				? currentIndex
				: runEnd,
		);
		runStart = index;
	}

	const entries: HistoryEntry[] = representatives.map(
		(timelineIndex, order) => {
			const kind: HistoryEntryKind =
				timelineIndex === currentIndex
					? "current"
					: timelineIndex < currentIndex
						? "past"
						: "future";
			const steps =
				kind === "current"
					? 0
					: kind === "past"
						? pastCount - timelineIndex
						: timelineIndex - currentIndex;
			// The oldest row is only the design's real starting point while the
			// ring buffer still holds it; at the limit zundo has already shifted
			// earlier snapshots off, so claiming "starting point" would be a lie.
			const label =
				order === 0
					? historyTruncated
						? GENERIC
						: { key: "history_initialState" as const }
					: describeHistoryStep(
							timeline[representatives[order - 1]],
							timeline[timelineIndex],
						);
			return { id: `${kind}:${steps}`, kind, steps, label };
		},
	);

	const past = entries.filter((entry) => entry.kind === "past");
	const current = entries.filter((entry) => entry.kind === "current");
	const future = entries.filter((entry) => entry.kind === "future");

	// Newest first: farthest redo -> ... -> nearest redo -> current -> undos.
	// Both caps keep the steps CLOSEST to the current position: the rows next
	// to "you are here" are the ones a user actually reaches for. For the redo
	// side that means taking the TAIL of the descending sort, not the head.
	// slice(-0) === slice(0) and would return EVERY row, so guard the cap.
	const futureNewestFirst =
		maxPerDirection <= 0
			? []
			: future
					.slice()
					.sort((a, b) => b.steps - a.steps)
					.slice(-maxPerDirection);
	const pastNewestFirst = past
		.slice()
		.sort((a, b) => a.steps - b.steps)
		.slice(0, maxPerDirection);

	return [...futureNewestFirst, ...current, ...pastNewestFirst];
}

/* ── Render boundary ─────────────────────────────────────── */

function formatArg(
	arg: HistoryLabelArg,
	t: Dictionary,
	unitSystem: UnitSystem,
	locale: string,
): string {
	switch (arg.kind) {
		case "text":
			return arg.value;
		case "i18n":
			return t[arg.key];
		case "lengthM":
			return formatLengthM(arg.value, unitSystem, locale);
		case "lengthMm":
			return formatLengthMm(arg.value, unitSystem, locale);
		case "areaM2":
			return formatAreaM2(arg.value, unitSystem, locale);
		case "dimensions":
			return `${formatLengthM(arg.width, unitSystem, locale)} × ${formatLengthM(
				arg.length,
				unitSystem,
				locale,
			)}`;
		case "percent":
			return percentFormatter(locale).format(arg.value);
		default:
			return `${wholeNumberFormatter(locale).format(arg.value)}°`;
	}
}

/**
 * Resolve a label to display text. Pure; the unit system and locale are
 * applied here and nowhere upstream.
 */
export function formatHistoryStepLabel(
	label: HistoryStepLabel,
	t: Dictionary,
	unitSystem: UnitSystem,
	locale: string,
): string {
	let text: string = t[label.key];
	if (!label.args) return text;
	for (const [name, arg] of Object.entries(label.args)) {
		text = text.replaceAll(`{${name}}`, formatArg(arg, t, unitSystem, locale));
	}
	return text;
}
